import React from 'react'
import { MoreDropdown } from 'shared/components/Dropdown/MoreDropdown'

import { Handler } from '../../../helpers/typeHelper'
import {
  ApplicablePolicyActions,
  DeductionConfigStatus,
  DropdownConfig,
  Repayment,
  RepaymentPolicyStatus,
} from '../../../models/repayments'
import { useUserProvider } from '../../Context/UserContext'
import { PolicyDetailsDialog } from './PolicyDetailsDialog'

interface PolicyDetailsDropdownProps {
  policy: Repayment
  refetchPolicy: () => void
  handlePolicyRedirect: Handler
  handleRefinanceRedirect: Handler
  isVendor: boolean
}

export function PolicyDetailsDropdown({
  policy,
  refetchPolicy,
  handlePolicyRedirect,
  handleRefinanceRedirect,
  isVendor,
}: PolicyDetailsDropdownProps) {
  const { showBanner } = useUserProvider()
  const [dialogStatus, setDialogStatus] = React.useState<RepaymentPolicyStatus | 'DEDUCTION_CREDIT'>(
    RepaymentPolicyStatus.ACTIVE
  )
  const [dialogOpen, setDialogOpen] = React.useState<boolean>(false)
  const status = policy?.repayment_details.policy_status ?? RepaymentPolicyStatus.DRAFT
  const scheduledConfigs = policy?.repayment_details.deduction_configs.filter(
    (config) => config.status === DeductionConfigStatus.SCHEDULED
  )

  const handleDialogStatus = (status: RepaymentPolicyStatus | 'DEDUCTION_CREDIT') => {
    setDialogStatus(status)
    setDialogOpen(true)
  }

  const dropdownOptions = [
    {
      key: RepaymentPolicyStatus.ACTIVE,
      label:
        status === RepaymentPolicyStatus.DRAFT
          ? // special case for draft -> active
            'Activate collection'
          : DropdownConfig[RepaymentPolicyStatus.ACTIVE].label,
      onClick: () => handleDialogStatus(RepaymentPolicyStatus.ACTIVE),
    },
    {
      key: RepaymentPolicyStatus.PAUSED,
      label: DropdownConfig[RepaymentPolicyStatus.PAUSED].label,
      onClick: () => handleDialogStatus(RepaymentPolicyStatus.PAUSED),
    },
    {
      key: RepaymentPolicyStatus.FINISHED,
      label: DropdownConfig[RepaymentPolicyStatus.FINISHED].label,
      onClick: () => handleDialogStatus(RepaymentPolicyStatus.FINISHED),
    },
    {
      key: RepaymentPolicyStatus.STOP_BILLING_AND_COLLECTION,
      label: DropdownConfig[RepaymentPolicyStatus.STOP_BILLING_AND_COLLECTION].label,
      onClick: () => handleDialogStatus(RepaymentPolicyStatus.STOP_BILLING_AND_COLLECTION),
    },
    {
      key: 'DEDUCTION_CREDIT',
      label: 'Credit policy',
      onClick: () => handleDialogStatus('DEDUCTION_CREDIT'),
    },
    {
      key: RepaymentPolicyStatus.TERMINATED,
      label: DropdownConfig[RepaymentPolicyStatus.TERMINATED].label,
      onClick: () => handleDialogStatus(RepaymentPolicyStatus.TERMINATED),
    },
    {
      key: 'REFINANCE',
      label: 'Refinance',
      onClick: () => {
        if (scheduledConfigs.filter((config) => config.status === DeductionConfigStatus.SCHEDULED).length) {
          // TODO: scheduled config is sometimes a correction
          showBanner('error', { message: 'Please cancel the scheduled refinancing before refinance again.' })
        } else {
          handleRefinanceRedirect()
        }
      },
    },
  ].filter(
    (option) =>
      option.key === 'DEDUCTION_CREDIT' ||
      option.key === 'REFINANCE' ||
      ApplicablePolicyActions[status].includes(option.key as RepaymentPolicyStatus)
  )

  return (
    <>
      <MoreDropdown
        options={dropdownOptions}
        ariaLabel="More options for card"
        align="right"
        width={220}
        maxHeight={600}
      />
      <PolicyDetailsDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        policy={policy}
        dialogStatus={dialogStatus}
        refetchPolicy={refetchPolicy}
        handlePolicyRedirect={handlePolicyRedirect}
        isVendor={isVendor}
      />
    </>
  )
}
